import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CheckSquare, At, FirstAid, CheckCircle, Checks } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { UserAvatar } from "./UserAvatar";
import { useNotifications, useNotificationPolling, refreshNotifications, markNotificationRead, markAllNotificationsRead } from "../lib/notifications";
import { relativeTime } from "../lib/theme";

const KIND = {
  task_assigned: { Icon: CheckSquare, color: "#2F7DF6" },
  task_completed: { Icon: CheckCircle, color: "#34C759" },
  mention: { Icon: At, color: "#AF52DE" },
  case_update: { Icon: FirstAid, color: "#F26B0F" },
  case_resolved: { Icon: CheckCircle, color: "#34C759" },
};
const isToday = (iso) => new Date(iso).toDateString() === new Date().toDateString();

export function NotificationItem({ n, onOpen, testPrefix = "notification", className = "" }) {
  const k = KIND[n.kind] || KIND.case_update;
  return (
    <li className={className}>
      <button type="button" onClick={() => onOpen(n)} data-testid={`${testPrefix}-${n.id}`} data-read={n.read}
        className="w-full flex items-start gap-3 px-3 py-3 rounded-2xl text-left hover:bg-muted/60 transition-colors">
        <span className="relative shrink-0">
          <UserAvatar user={n.actor || { id: "x", name: "Removed member" }} className="h-9 w-9 text-xs" />
          <span className="absolute -bottom-1 -right-1 h-[18px] w-[18px] rounded-full ring-2 ring-popover flex items-center justify-center" style={{ background: k.color }}>
            <k.Icon weight="bold" className="h-2.5 w-2.5 text-white" />
          </span>
        </span>
        <span className="min-w-0 flex-1">
          <span className={`block text-sm leading-5 ${n.read ? "text-foreground/80" : "font-semibold"}`}>{n.title}</span>
          {n.body && <span className="block mt-0.5 text-xs leading-4 text-muted-foreground line-clamp-2">{n.body}</span>}
          <span className="block mt-1 text-[11px] text-muted-foreground/80">{relativeTime(n.created_at)}</span>
        </span>
        {!n.read && <span aria-label="Unread" className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />}
      </button>
    </li>
  );
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { items, unread } = useNotifications();
  useNotificationPolling();
  useEffect(() => { if (open) refreshNotifications(); }, [open]);

  const openItem = (n) => {
    setOpen(false);
    markNotificationRead(n);
    if (n.link) navigate(n.link);
  };
  const readAll = markAllNotificationsRead;

  const groups = [["Today", (items || []).filter((n) => isToday(n.created_at))], ["Earlier", (items || []).filter((n) => !isToday(n.created_at))]].filter(([, xs]) => xs.length);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="notifications-bell" aria-label={unread ? `${unread} unread notifications` : "Notifications"}
          className="relative h-9 w-9 rounded-full inline-flex items-center justify-center text-foreground/80 hover:text-foreground hover:bg-muted transition-colors">
          <Bell weight={unread ? "fill" : "regular"} className="h-[18px] w-[18px]" />
          {unread > 0 && (
            <span data-testid="notifications-badge" className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 rounded-full bg-[#E0242B] text-white text-[9px] font-semibold leading-[16px] text-center ring-2 ring-background tabular-nums">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={10} collisionPadding={12} className="w-[calc(100vw-24px)] sm:w-96 p-0 rounded-3xl overflow-hidden" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-5 pt-4 pb-3">
          <p className="text-base font-semibold tracking-tight">Notifications</p>
          {unread > 0 && (
            <button type="button" onClick={readAll} data-testid="notifications-read-all" className="text-xs font-medium text-primary hover:text-primary/80 inline-flex items-center gap-1">
              <Checks className="h-3.5 w-3.5" />Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[min(70vh,32rem)] overflow-y-auto px-2 pb-2">
          {items === null && <p className="px-3 py-10 text-center text-xs text-muted-foreground">Loading…</p>}
          {items?.length === 0 && (
            <div className="px-6 py-12 text-center" data-testid="notifications-empty">
              <Bell weight="duotone" className="h-8 w-8 mx-auto text-muted-foreground/60" />
              <p className="mt-2 text-sm font-medium">No notifications yet</p>
              <p className="mt-0.5 text-xs text-muted-foreground">You'll hear about tasks, @mentions and acute cases you're part of.</p>
            </div>
          )}
          {groups.map(([label, xs]) => (
            <div key={label} className="pt-1">
              <p className="px-3 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{label}</p>
              <ul>{xs.map((n) => <NotificationItem key={n.id} n={n} onOpen={openItem} />)}</ul>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
